import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utilitaire pour fusionner les classes Tailwind
 */
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Formate un nombre en devise
 */
export function formatCurrency(value: number, currency: string = 'USD', language: string = 'fr'): string {
    const localeMap: Record<string, string> = { 'fr': 'fr-FR', 'en': 'en-US', 'pt': 'pt-PT', 'es': 'es-ES', 'de': 'de-DE', 'it': 'it-IT' };
    return new Intl.NumberFormat(localeMap[language] || 'fr-FR', {
        style: 'currency',
        currency,
    }).format(value);
}

/**
 * Formate un pourcentage
 */
export function formatPercent(value: number, decimals: number = 2): string {
    return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

/**
 * Formate une date
 */
export function formatDate(date: Date, format: 'short' | 'long' = 'short', language: string = 'fr'): string {
    const localeMap: Record<string, string> = { 'fr': 'fr-FR', 'en': 'en-US', 'pt': 'pt-PT', 'es': 'es-ES', 'de': 'de-DE', 'it': 'it-IT' };
    const locale = localeMap[language] || 'fr-FR';

    if (format === 'short') {
        return new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(date);
    }

    return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

/**
 * Calcule le nombre de jours entre deux dates
 */
export function daysBetween(date1: Date, date2: Date): number {
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
}

/**
 * Génère un ID unique
 */
export function generateId(): string {
    return crypto.randomUUID();
}

/**
 * Tronque un texte
 */
export function truncate(text: string, length: number): string {
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
}

/**
 * Valide un email
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Détermine la couleur en fonction de la valeur (positif/négatif)
 */
export function getValueColor(value: number): string {
    if (value > 0) return 'text-success-400';
    if (value < 0) return 'text-danger-400';
    return 'text-dark-400';
}

/**
 * Calcule le Risk/Reward ratio
 */
export function calculateRR(entry: number, stopLoss: number, takeProfit: number): number {
    const risk = Math.abs(entry - stopLoss);
    const reward = Math.abs(takeProfit - entry);
    return risk > 0 ? reward / risk : 0;
}
/**
 * Extrait l'ID d'une vidéo YouTube et retourne l'URL d'embed
 */
export function getYouTubeEmbedUrl(url: string | null | undefined): string | null {
    if (!url) return null;

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return null;

    try {
        // Regex exhaustive pour YouTube (shorts, live, watch, youtu.be, embed, etc)
        // Updated regex to be more robust for various YouTube URL formats
        const regExp = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|shorts\/|live\/)?([\w-]{11})(?:\S+)?/;
        const match = trimmedUrl.match(regExp);
        const videoId = (match && match[1].length === 11) ? match[1] : null;

        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
        }

        // Si c'est déjà un lien embed valide
        if (trimmedUrl.includes('youtube.com/embed/')) {
            // S'assurer qu'il y a les paramètres d'auto-play si on veut
            return trimmedUrl.includes('?') ? `${trimmedUrl}&autoplay=1` : `${trimmedUrl}?autoplay=1`;
        }

        return null;
    } catch {
        return null;
    }
}
/**
 * Formate le nom en majuscules
 */
export function formatLastName(name: string): string {
    return name.trim().toUpperCase();
}

/**
 * Formate le prénom (Première lettre de chaque mot en majuscule)
 * Gère les espaces et les tirets pour les prénoms composés
 */
export function formatFirstName(name: string): string {
    if (!name) return '';
    return name
        .trim()
        .toLowerCase()
        .split(/(\s|-)/) // Sépare par espace ou tiret tout en gardant le séparateur
        .map(part => {
            if (part === ' ' || part === '-') return part;
            return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join('');
}
/**
 * Formatage du nom complet (Prénom en Title Case, NOM en MAJUSCULES)
 */
export function formatFullName(firstName: string, lastName: string): string {
    return `${formatFirstName(firstName)} ${formatLastName(lastName)}`;
}
/**
 * Normalise les traductions (gère les cas où les traductions sont stockées sous forme d'array ou d'objet avec clés numériques)
 */
export function normalizeTranslations(translations: any): Record<string, any> {
    if (!translations) return {};

    const langMap: Record<string, string> = {
        'english': 'en', 'en': 'en',
        'deutsch': 'de', 'de': 'de',
        'português': 'pt', 'portuguese': 'pt', 'pt': 'pt',
        'español': 'es', 'spanish': 'es', 'es': 'es',
        'italiano': 'it', 'italian': 'it', 'it': 'it',
        'français': 'fr', 'french': 'fr', 'fr': 'fr'
    };

    let base = {};

    // 1. Aplatissement des structures bizarres (Array ou Clés numériques de Firebase)
    if (Array.isArray(translations)) {
        base = translations.reduce((acc, curr) => {
            if (typeof curr === 'object' && curr !== null) return { ...acc, ...curr };
            return acc;
        }, {});
    } else if (typeof translations === 'object' && translations !== null) {
        const keys = Object.keys(translations);
        const hasNumericKeys = keys.some(k => !isNaN(Number(k)));
        if (hasNumericKeys) {
            base = keys.reduce((acc, key) => {
                const val = translations[key];
                if (typeof val === 'object' && val !== null) return { ...acc, ...val };
                return acc;
            }, {});
        } else {
            base = translations;
        }
    }

    // 2. Normalisation des clés de langue uniquement au premier niveau
    const final: Record<string, any> = {};
    Object.entries(base).forEach(([key, value]) => {
        const normalizedKey = langMap[key.toLowerCase()] || key.toLowerCase();
        final[normalizedKey] = value;
    });

    return final;
}
